import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isStepComplete } from "../../components/Live/utils/feuilleMatch.js";
import SoccerFeuilleMatch from "./SoccerFeuilleMatch.js";
import RugbyFeuilleMatch from "./RugbyFeuilleMatch.js";
import BasketFeuilleMatch from "./BasketFeuilleMatch.js";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { updateMatch } from "../../actions/matchs.js";
import { useDispatch } from "react-redux";
import { useCompositionListFeuilleMatch } from "./useCompositionListFeuilleMatch.js";
import HandballFeuilleMatch from "./HandballFeuilleMatch.js";
import DsasButton from "../../v1/components/DsasButton/DsasButton.js";
import useStyles from '../../styles/global-mui.js';

const hasId = (x) => x && typeof x === 'object' && !!x._id;
const cleanList = (arr) => Array.isArray(arr) ? arr.filter(hasId) : [];

const buildPayload = (md) => ({
  homeHolders: cleanList(md.homeHolders),
  awayHolders: cleanList(md.awayHolders),
  homeSubstitutes: cleanList(md.homeSubstitutes),
  awaySubstitutes: cleanList(md.awaySubstitutes),
  homeComposition: md.homeComposition,
  awayComposition: md.awayComposition,
  homeCoach: md.homeCoach,
  awayCoach: md.awayCoach,
  provider: md.provider,
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        // ✅ pas de Typography ici pour éviter le wrapping texte
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

//start index for default numbering of substitutes
// each discipline has its own starting number
const startSubstitutesNumber = (discipline) => { 
  switch (discipline) {
    case 'soccer':
      return 12;
    case 'rugby':
      return 16;
    case 'basket':
      return 6;
    case 'handball':
      return 8;
    default:
      return 12;
  }
};

// utilitaire pour placeholders avec objets distincts + position déjà renseignée
const placeholders = (n, generateDefaultNumbers, startNumber) => Array.from({ length: n }, (_, i) => ({ position: i , numero: generateDefaultNumbers ? (startNumber ? startNumber+i : i + 1) : undefined }));

const FeuilleMatchCreation = ({ match, close }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [value, setValue] = React.useState(0);
  const [completeStep, setCompleteStep] = useState([false, false]);
  const [matchData, setMatchData] = useState(undefined);

  const handleChange = (_e, newValue) => setValue(newValue);

  useEffect(() => {
    const discipline = match?.competition?.discipline || match?.discipline;

    const initialMatchData = {
      provider: match.provider,
      competition: match.competition,
      discipline,
      season: '',
      day: match.day,
      dateTime: match.dateTime,

      homeTeam: match.homeTeam?._id,
      homeCoach: match.homeCoach,
      homeSubstitutes:
        (match.homeSubstitutes && match.homeSubstitutes.length > 0)
          ? [...match.homeSubstitutes]
          : placeholders(5, true, startSubstitutesNumber(discipline)),

      awayTeam: match.awayTeam?._id,
      awayCoach: match.awayCoach,
      awaySubstitutes:
        (match.awaySubstitutes && match.awaySubstitutes.length > 0)
          ? [...match.awaySubstitutes]
          : placeholders(5, true, startSubstitutesNumber(discipline)),
    };

    // holders + composition par sport
    if (discipline === 'soccer') {
      initialMatchData.homeHolders =
        (match.homeHolders && match.homeHolders.length > 0)
          ? [...match.homeHolders]
          : placeholders(11);
      initialMatchData.awayHolders =
        (match.awayHolders && match.awayHolders.length > 0)
          ? [...match.awayHolders]
          : placeholders(11);
      initialMatchData.homeComposition = match.homeComposition || '__default__';
      initialMatchData.awayComposition = match.awayComposition || '__default__';
    } else if (discipline === 'rugby') {
      initialMatchData.homeHolders =
        (match.homeHolders && match.homeHolders.length > 0)
          ? [...match.homeHolders]
          : placeholders(15, true);
      initialMatchData.awayHolders =
        (match.awayHolders && match.awayHolders.length > 0)
          ? [...match.awayHolders]
          : placeholders(15, true);
      initialMatchData.homeComposition = match.homeComposition || 'rugby15';
      initialMatchData.awayComposition = match.awayComposition || 'rugby15';
    } else if (discipline === 'basket') {
      initialMatchData.homeHolders =
        (match.homeHolders && match.homeHolders.length > 0)
          ? [...match.homeHolders]
          : placeholders(5, true);
      initialMatchData.awayHolders =
        (match.awayHolders && match.awayHolders.length > 0)
          ? [...match.awayHolders]
          : placeholders(5, true);
      initialMatchData.homeComposition = match.homeComposition || 'basket5';
      initialMatchData.awayComposition = match.awayComposition || 'basket5';
    } else if (discipline === 'handball') {
      initialMatchData.homeHolders =
        (match.homeHolders && match.homeHolders.length > 0)
          ? [...match.homeHolders]
          : placeholders(7, true);
      initialMatchData.awayHolders =
        (match.awayHolders && match.awayHolders.length > 0)
          ? [...match.awayHolders]
          : placeholders(7, true);
      initialMatchData.homeComposition = match.homeComposition || 'handball7';
      initialMatchData.awayComposition = match.awayComposition || 'handball7';
    }

    // console.log('Initial match data for feuille de match :', initialMatchData);

    setMatchData(prev => {
      // reset complet si premier rendu ou changement de match
      if (!prev || prev.idMatch !== match.idMatch) {
        return { ...initialMatchData, idMatch: match.idMatch };
      }

      // sinon, merge en préservant la saisie utilisateur
      const mergedData = {
        ...initialMatchData,
        idMatch: match.idMatch,

        // compositions
        homeComposition: prev.homeComposition || initialMatchData.homeComposition,
        awayComposition: prev.awayComposition || initialMatchData.awayComposition,

        // coaches
        homeCoach: prev.homeCoach ?? initialMatchData.homeCoach,
        awayCoach: prev.awayCoach ?? initialMatchData.awayCoach,

        // titulaires
        homeHolders: (prev.homeHolders && prev.homeHolders.length > 0)
          ? prev.homeHolders
          : initialMatchData.homeHolders,
        awayHolders: (prev.awayHolders && prev.awayHolders.length > 0)
          ? prev.awayHolders
          : initialMatchData.awayHolders,

        // remplaçants (on garde si au moins un objet non vide)
        homeSubstitutes:
          (prev.homeSubstitutes && prev.homeSubstitutes.length > 0 && prev.homeSubstitutes.some(x => Object.keys(x || {}).length > 0))
            ? prev.homeSubstitutes
            : initialMatchData.homeSubstitutes,
        awaySubstitutes:
          (prev.awaySubstitutes && prev.awaySubstitutes.length > 0 && prev.awaySubstitutes.some(x => Object.keys(x || {}).length > 0))
            ? prev.awaySubstitutes
            : initialMatchData.awaySubstitutes,
      };
      return mergedData;
    });
  }, [match]);

  // const handleClickNext = () => {
  //   dispatch(updateMatch(
  //     match.idMatch,
  //     {
  //       homeHolders: matchData.homeHolders,
  //       awayHolders: matchData.awayHolders,
  //       homeSubstitutes: matchData.homeSubstitutes,
  //       awaySubstitutes: matchData.awaySubstitutes,
  //       homeComposition: matchData.homeComposition,
  //       awayComposition: matchData.awayComposition,
  //       homeCoach: matchData.homeCoach,
  //       awayCoach: matchData.awayCoach,
  //       provider: matchData.provider
  //     },
  //     'composition'
  //   ));
  //   close();
  // };

  const handleClickNext = () => {
    const payload = buildPayload(matchData); // 🔽 placeholders supprimés ici

    dispatch(updateMatch(
      match.idMatch,
      payload,
      'composition'
    ));
    close();
  };

  const onCompleteStep = useCallback((activeStep) => {
    setCompleteStep(s => {
      const next = [...s];
      next[activeStep] = true;
      return next;
    });
  }, []);

  const onUncompleteStep = useCallback((activeStep) => {
    setCompleteStep(s => {
      const next = [...s];
      next[activeStep] = false;
      return next;
    });
  }, []);

  if (!matchData) return <CircularProgress />;

  return (
    <>
      <Box sx={{ width: '100%', position: 'relative', mt: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs">
            <Tab label={match.homeTeam.name} {...a11yProps(0)} />
            <Tab label={match.awayTeam.name} {...a11yProps(1)} />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <FeuilleMatch
            match={match}
            uncompleteStep={onUncompleteStep}
            completeStep={onCompleteStep}
            matchData={matchData}
            setMatchData={setMatchData}
            activeStep={0}
          />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <FeuilleMatch
            match={match}
            uncompleteStep={onUncompleteStep}
            completeStep={onCompleteStep}
            matchData={matchData}
            setMatchData={setMatchData}
            activeStep={1}
          />
        </TabPanel>
      </Box>

      <Box className='large-padding'>
        {(!completeStep[0] || !completeStep[1]) && (
          <Box className='centered-wrapper'>
            <Typography
              className={classes.customTypoColor}
              padding='10px'
              justifySelf='center'
              fontSize="small"
              fontStyle="italic"
            >
              Veuillez saisir tous les titulaires pour terminer
            </Typography>
          </Box>
        )}

        <Box className='centered-wrapper'>
          <DsasButton
            label={value === 0 ? 'Terminer (équipe domicile)' : 'Terminer (équipe extérieur)'}
            variant='alternative'
            disabled={(value === 0 && !completeStep[0]) || (value === 1 && !completeStep[1])}
            onClick={handleClickNext}
          />
        </Box>
      </Box>
    </>
  );
};

const FeuilleMatch = ({ match, completeStep, uncompleteStep, matchData, setMatchData, activeStep }) => {
  //console.log('Rendering FeuilleMatch with matchData in FeuilleMatchCreation:', matchData);
  const [compositions] = useCompositionListFeuilleMatch(matchData.discipline);

  const holdersLabel = activeStep === 0 ? 'homeHolders' : 'awayHolders';

  // ✅ mapping robuste + fallback
  const Component = useMemo(() => {
    const disc = matchData?.competition?.discipline || matchData?.discipline || match?.discipline;
    const map = {
      rugby: RugbyFeuilleMatch,
      soccer: SoccerFeuilleMatch,
      basket: BasketFeuilleMatch,
      handball: HandballFeuilleMatch,
    };
    return map[disc] || SoccerFeuilleMatch;
  }, [matchData?.competition?.discipline, matchData?.discipline, match?.discipline]);

  const isCompletedStep = useCallback(() => {
    const { [holdersLabel]: holders } = matchData;
    const completed = isStepComplete(holders, matchData.discipline);
    if (completed) completeStep(activeStep);
    else uncompleteStep(activeStep);
  }, [activeStep, completeStep, holdersLabel, matchData, uncompleteStep]);

  useEffect(() => {
    isCompletedStep();
  }, [matchData, activeStep, isCompletedStep]);

  return (
    <Component
      match={match}
      completeStep={completeStep}
      matchData={matchData}
      setMatchData={setMatchData}
      isHome={activeStep === 0}
      compositionsList={compositions}
    />
  );
};

export default FeuilleMatchCreation;
